/* eslint react-hooks/exhaustive-deps: "warn" */

import { type CSSProperties, type ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './ScreeningSection.module.scss';

type Translate = (key: string) => string;

type Point = { term: string; detail: ReactNode };

const getPoints = (t: Translate): Point[] => [
  {
    term: t('Who is screened'),
    detail: (
      <>
        <b>{t('Every entity')}</b> {t('under the global ultimate — and every affiliated person:')}{' '}
        <b>{t('directors, shareholders, and ultimate beneficial owners')}</b>.
      </>
    ),
  },
  {
    term: t('Checked against'),
    detail: (
      <>
        <b>OFAC SDN, EU Consolidated, UN Security Council, UK HMT</b> {t('sanctions lists, plus')}{' '}
        <b>{t('global PEP databases')}</b>.
      </>
    ),
  },
  {
    term: t('What you get'),
    detail: (
      <>
        {t('A')} <b>{t('clear or flagged status')}</b>{' '}
        {t('for every company and person — potential matches routed straight to a')} <b>{t('review queue')}</b>.
      </>
    ),
  },
];

type RowResult = 'clear' | 'review';
type RowStatus = 'idle' | 'scanning' | RowResult;

type TreeRow = { name: string; flag: string; indent: number; result: RowResult };

const TREE: TreeRow[] = [
  { name: 'Acme Group PLC', flag: '🇮🇪', indent: 0, result: 'clear' },
  { name: 'Acme Holdings UK Ltd', flag: '🇬🇧', indent: 1, result: 'clear' },
  { name: 'Acme Europe Holdco Ltd', flag: '🇬🇧', indent: 2, result: 'review' },
  { name: 'Acme Payments Ltd', flag: '🇬🇧', indent: 3, result: 'clear' },
];

const FINAL_STATS = { checks: 16, clear: 15, review: 1 };

const CountUp = ({ value }: { value: number | null }) => {
  const [display, setDisplay] = useState<number | null>(value);
  const prevRef = useRef<number | null>(value);
  const rafRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (value === null) {
      prevRef.current = null;
      setDisplay(null);
      return undefined;
    }

    const reducedMotion =
      typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reducedMotion) {
      prevRef.current = value;
      setDisplay(value);
      return undefined;
    }

    const from = prevRef.current ?? 0;
    const to = value;
    const duration = 450;
    const start = performance.now();

    const tick = (now: number) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - (1 - progress) ** 3;
      setDisplay(Math.round(from + (to - from) * eased));

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        prevRef.current = to;
      }
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current !== undefined) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [value]);

  return <>{display ?? '—'}</>;
};

export const ScreeningSection = () => {
  const { t } = useInt();
  const points = getPoints(t);
  const sectionRef = useRef<HTMLDivElement>(null);
  const copyRef = useRef<HTMLDivElement>(null);
  const copyRevealed = useIntersection(copyRef, { threshold: 0.2 });
  const demoRef = useRef<HTMLDivElement>(null);
  const demoRevealed = useIntersection(demoRef, { threshold: 0.15 });
  const cardRef = useRef<HTMLDivElement>(null);
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const handleCardMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const card = cardRef.current;

    if (!card || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    const rect = card.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    const maxTilt = 6;

    card.style.transform = `perspective(1000px) rotateX(${(-py * maxTilt).toFixed(2)}deg) rotateY(${(px * maxTilt).toFixed(2)}deg) translateY(-4px)`;
    card.style.setProperty('--glareX', `${(px + 0.5) * 100}%`);
    card.style.setProperty('--glareY', `${(py + 0.5) * 100}%`);
  };

  const handleCardMouseLeave = () => {
    const card = cardRef.current;

    if (!card) {
      return;
    }

    card.style.transform = '';
  };

  const [statuses, setStatuses] = useState<RowStatus[]>(TREE.map(() => 'idle'));
  const [stats, setStats] = useState<{ checks: number | null; clear: number | null; review: number | null }>({
    checks: null,
    clear: null,
    review: null,
  });
  const [running, setRunning] = useState(false);
  const [hasRun, setHasRun] = useState(false);

  const clearTimeouts = () => {
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
  };

  const runScreening = useCallback(() => {
    clearTimeouts();
    setRunning(true);
    setHasRun(true);
    setStats({ checks: null, clear: null, review: null });
    setStatuses(TREE.map(() => 'scanning'));

    const reducedMotion =
      typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    TREE.forEach((row, i) => {
      const id = setTimeout(
        () => {
          setStatuses((prev) => {
            const next = [...prev];
            next[i] = row.result;
            return next;
          });

          setStats((prev) => ({ ...prev, checks: (i + 1) * 4 }));

          if (i === TREE.length - 1) {
            setStats(FINAL_STATS);
            setRunning(false);
          }
        },
        reducedMotion ? 0 : 450 + i * 420,
      );

      timeoutsRef.current.push(id);
    });
  }, []);

  useEffect(() => {
    const node = sectionRef.current;

    if (!node) {
      return undefined;
    }

    if (!('IntersectionObserver' in window)) {
      runScreening();
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            runScreening();
            observer.disconnect();
          }
        });
      },
      { threshold: 0.35 },
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, [runScreening]);

  useEffect(() => clearTimeouts, []);

  return (
    <Section className={styles.root}>
      <div className={styles.container} ref={sectionRef}>
        <div className={cn(styles.copy, copyRevealed && styles.copyIn)} ref={copyRef}>
          <TitleBlock
            align="left"
            title={t('Screen every company in the group — and the people behind them')}
            description={t(
              'Point it at any company. We resolve the full group and run one PEP & sanctions check across every entity in it — and every director, shareholder, and UBO connected to them.',
            )}
          />
          <dl className={styles.points}>
            {points.map((point, index) => (
              <div
                key={point.term}
                className={styles.point}
                style={{ '--delay': `${index * 100}ms` } as CSSProperties}
              >
                <dt>{point.term}</dt>
                <dd>{point.detail}</dd>
              </div>
            ))}
          </dl>
        </div>

        <div className={cn(styles.demo, demoRevealed && styles.demoIn)} ref={demoRef}>
          <div
            className={cn(styles.card, running && styles.cardScanning)}
            ref={cardRef}
            onMouseMove={handleCardMouseMove}
            onMouseLeave={handleCardMouseLeave}
          >
            <div className={styles.cardGlare} aria-hidden="true" />
            <div className={styles.cardHead}>
              <div>
                <div className={styles.cardTitle}>
                  Acme Group · {t('PEPs & Sanctions')}
                  {running && (
                    <span className={styles.liveDot}>
                      <span className={styles.liveDotPing} />
                    </span>
                  )}
                </div>
                <div className={styles.cardSub}>{t('4 entities · 12 officers & UBOs')}</div>
              </div>
              <button type="button" className={styles.cardBtn} onClick={runScreening} disabled={running}>
                {running ? t('Running…') : hasRun ? t('Run again') : t('Run group check')}
              </button>
            </div>

            <div className={styles.stats}>
              <div className={styles.stat}>
                <div className={styles.statValue}>
                  <CountUp value={stats.checks} />
                </div>
                <div className={styles.statLabel}>{t('Checks run')}</div>
              </div>
              <div className={styles.stat}>
                <div className={cn(styles.statValue, styles.statOk)}>
                  <CountUp value={stats.clear} />
                </div>
                <div className={styles.statLabel}>{t('Clear')}</div>
              </div>
              <div className={styles.stat}>
                <div className={cn(styles.statValue, styles.statWarn)}>
                  <CountUp value={stats.review} />
                </div>
                <div className={styles.statLabel}>{t('For review')}</div>
              </div>
            </div>

            <div className={cn(styles.tree, demoRevealed && styles.treeIn)}>
              {TREE.map((row, i) => {
                const status = statuses[i];
                const connectorX = 24 + (row.indent - 1) * 26 + 13;

                return (
                  <div
                    key={row.name}
                    className={cn(
                      styles.treeRow,
                      status === 'review' && styles.treeRowFlag,
                      row.indent > 0 && styles.treeRowNested,
                    )}
                    style={
                      {
                        paddingLeft: `${24 + row.indent * 26}px`,
                        '--connectorX': `${connectorX}px`,
                        '--delay': `${i * 90}ms`,
                      } as CSSProperties
                    }
                  >
                    <span className={styles.treeName}>
                      {row.name} <i>{row.flag}</i>
                    </span>
                    <span
                      className={cn(
                        styles.pill,
                        status === 'scanning' && styles.pillScanning,
                        status === 'clear' && styles.pillClear,
                        status === 'review' && styles.pillReview,
                      )}
                    >
                      {status === 'idle' && '—'}
                      {status === 'scanning' && t('Checking…')}
                      {status === 'clear' && t('Clear')}
                      {status === 'review' && t('1 match · review')}
                    </span>
                  </div>
                );
              })}
            </div>

            <div className={styles.cardFoot}>
              {t('OFAC SDN · EU Consolidated · UN Security Council · UK HMT · global PEP databases')}
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
};
